import React from 'react';
import { useTranslation } from 'react-i18next';

import { LineChart, Skeleton, DayBars, type ChartSeries, type DayBarSeries } from '../../../../components';
import { HOME_SERIES_COLORS } from '../../../../theme/tokens';
import type { HomePeriod, StatsFinancial } from '../../../../mock-data/types';
import { StatCard } from '../StatCard';
import { PrimaryStat } from '../PrimaryStat';
import { SplitStat } from '../SplitStat';
import { Legend } from '../Legend';
import { Inner, Grid, LeftCol, Divider, SplitRow, ChartCol, LegendWrap } from './FinancialPerformance.styles';

/** Compact money axis ("$700", "$1.2k", "$-341.3k") — abbreviates by magnitude so
 *  large NEGATIVE values (e.g. a loss-making GGR) compact too. */
const moneyFmt = (n: number): string => {
  const abs = Math.abs(n);
  return abs >= 1000 ? `$${n < 0 ? '-' : ''}${Math.round(abs / 100) / 10}k` : `$${Math.round(n)}`;
};

// Mirrors the LineChart `height` so the placeholder reserves the same band.
const CHART_HEIGHT = 170;

export type FinancialPerformanceProps = {
  data: StatsFinancial;
  /** The selected range — drives whether the trend chart or the day-comparison bars
   *  show (Today/Yesterday have no usable hourly slices). */
  interval: HomePeriod;
  /** While true, the figures + chart show placeholders (the endpoint is loading). */
  loading?: boolean;
};

/**
 * "Financial Performance" — the Gross Revenue lead with Stakes / Wins split
 * stats, beside a three-series GGR / Stakes / Wins chart (Wins dashed). The time
 * range is driven globally from the page header; on the Today / Yesterday ranges
 * the chart collapses to a prior-day vs current-day {@link DayBars} comparison.
 */
export const FinancialPerformance = ({ data, interval, loading = false }: FinancialPerformanceProps): React.ReactElement => {
  const { t } = useTranslation();
  const dayView = interval === 'TODAY' || interval === 'YESTERDAY';
  const series: ChartSeries[] = [
    { name: t('GGR'), color: HOME_SERIES_COLORS.ggr, points: data.chart.ggr },
    { name: t('Stakes'), color: HOME_SERIES_COLORS.stakes, points: data.chart.stakes },
    { name: t('Wins'), color: HOME_SERIES_COLORS.wins, points: data.chart.wins, dashed: true },
  ];
  const dayBars: DayBarSeries[] = [
    { name: t('GGR'), color: HOME_SERIES_COLORS.ggr, ...data.days.ggr },
    { name: t('Stakes'), color: HOME_SERIES_COLORS.stakes, ...data.days.stakes },
    { name: t('Wins'), color: HOME_SERIES_COLORS.wins, dashed: true, ...data.days.wins },
  ];
  return (
    <StatCard
      title={t('Financial Performance')}
      sub={t('Tracking the conversion from Handle to Gross Profit')}
    >
      <Inner>
        <Grid>
          <LeftCol>
            <PrimaryStat data={data.grossRevenue} loading={loading} />
            <Divider />
            <SplitRow>
              <SplitStat data={data.stakes} loading={loading} />
              <SplitStat data={data.wins} loading={loading} />
            </SplitRow>
          </LeftCol>
          <ChartCol>
            {loading ? (
              <Skeleton block height={CHART_HEIGHT} radius={12} />
            ) : dayView ? (
              <DayBars series={dayBars} view={interval === 'TODAY' ? 'today' : 'yesterday'} height={CHART_HEIGHT} formatValue={moneyFmt} />
            ) : (
              <LineChart series={series} xLabels={data.chart.labels} pointLabels={data.chart.pointLabels} height={CHART_HEIGHT} yTicks={3} formatY={moneyFmt} />
            )}
            <LegendWrap>
              <Legend
                items={[
                  { name: t('GGR'), color: HOME_SERIES_COLORS.ggr },
                  { name: t('Stakes'), color: HOME_SERIES_COLORS.stakes },
                  { name: t('Wins'), color: HOME_SERIES_COLORS.wins, dashed: true },
                ]}
              />
            </LegendWrap>
          </ChartCol>
        </Grid>
      </Inner>
    </StatCard>
  );
};

export default FinancialPerformance;
